import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Alert, Spinner } from 'flowbite-react';
import { FiUserPlus, FiCheck } from 'react-icons/fi';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { signInWithRedirect } from '@aws-amplify/auth';
import * as sched from 'sched';
import { useAuth } from '../hooks/useAuth';
import { buildWorkspaceUrl } from '../routes/workspaceUrl';
import UserProfileForm from '../components/forms/UserProfileForm';
import logo from '../assets/logo-2024-12-04-no-bg.webp';

const InvitePage: React.FC = () => {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const { signOut, user } = useAuthenticator((context) => [context.user]);
  const { user: authUser, token, client } = useAuth();
  
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptanceResult, setAcceptanceResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  // Store the invitation token in localStorage for post-auth redirect
  useEffect(() => {
    if (tokenId) {
      localStorage.setItem('pendingInviteToken', tokenId);
    }
  }, [tokenId]);

  // Handle invitation acceptance after authentication
  useEffect(() => {
    const handleAcceptInvite = async () => {
      if (!user || !authUser || !token || !client || !tokenId) return;
      
      // Check if we've already processed this invitation
      if (acceptanceResult) return;

      setIsAccepting(true);
      setError(null);

      try {
        const acceptRequest: sched.TokenAcceptInviteRequest = {
          token_id: tokenId,
        };

        const result = await sched.WrappedApiClient.accept_invite(
          client,
          token,
          acceptRequest
        );

        setAcceptanceResult(result);
        setShowProfileSetup(true);
        
        // Clear the pending invite token
        localStorage.removeItem('pendingInviteToken');
      } catch (err) {
        console.error('Failed to accept invitation:', err);
        setError('Failed to accept invitation. The link may be invalid or expired.');
      } finally {
        setIsAccepting(false);
      }
    };

    if (user && authUser && token && client && tokenId && !isAccepting && !acceptanceResult) {
      handleAcceptInvite();
    }
  }, [user, authUser, token, client, tokenId, isAccepting, acceptanceResult]);

  const handleProfileSetupComplete = () => {
    if (acceptanceResult?.workspace_authorization?.workspace_id) {
      // Redirect to the workspace
      navigate(buildWorkspaceUrl(acceptanceResult.workspace_authorization.workspace_id, { kind: 'workspace-root' }));
    } else {
      // Fallback to workspaces list
      navigate('/workspaces');
    }
  };

  const handleSignIn = async () => {
    try {
      // Redirect to AWS Cognito for authentication
      await signInWithRedirect();
    } catch (error) {
      console.error('Error initiating sign-in:', error);
      setError('Failed to initiate sign-in. Please try again.');
    }
  };

  if (!tokenId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center">
            <Alert color="failure">
              Invalid invitation link. Please check the URL and try again.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center space-y-6">
            <img src={logo} alt="Logo" className="h-16 mx-auto" />
            
            <div>
              <FiUserPlus className="w-16 h-16 mx-auto text-blue-600 dark:text-blue-400 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                You're Invited!
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                You've been invited to join a workspace. Sign in or create an account to accept the invitation.
              </p>
            </div>

            <div className="space-y-3">
              <Button onClick={handleSignIn} className="w-full">
                Sign In / Sign Up
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                After signing in, you'll automatically join the workspace and can set up your profile.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isAccepting) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center space-y-4">
            <Spinner size="xl" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Accepting Invitation...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we process your invitation.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center space-y-4">
            <Alert color="failure">
              {error}
            </Alert>
            <div className="space-y-2">
              <Button color="gray" onClick={() => navigate('/workspaces')}>
                Go to Workspaces
              </Button>
              <Button color="gray" onClick={signOut}>
                Sign Out
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (showProfileSetup && acceptanceResult) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <div className="text-center space-y-6">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mx-auto">
              <FiCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                🎉 Welcome to the Workspace!
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                You've successfully joined the workspace. Let's set up your profile to get started.
              </p>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <UserProfileForm
                isOpen={true}
                onClose={handleProfileSetupComplete} // Allow skipping profile setup
                onSuccess={handleProfileSetupComplete}
              />
            </div>
            
            {/* Skip Profile Setup Button */}
            <div className="text-center pt-4">
              <Button
                color="light"
                onClick={handleProfileSetupComplete}
                className="text-sm"
              >
                Skip Profile Setup
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                You can update your profile later from the user menu
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return null;
};

export default InvitePage;
